/**
 * `useAssociationMatchers` — ergonomic hook that combines `useAssociationTypes`
 * and `useObjectTypes` and pre-binds the matchers in `utils/associationMatching.ts`.
 *
 * Use this in any call site that needs to filter/find association types by
 * source or target entity, e.g.
 *
 *   const { associationTypes, matchesSource, matchesTarget } =
 *     useAssociationMatchers(orgId);
 *
 *   const hasNoteAssoc = associationTypes.some(at =>
 *     at.source_entity_type_name === 'ahoy_note' &&
 *     matchesTarget(at, object.entity_type_name)
 *   );
 *
 * Why this exists: the schema supports both type-based and kind-based association
 * matching, but the frontend had historically only matched type names directly.
 * That silently lost every kind-based association (notes ↔ contacts, files ↔ contacts,
 * etc.), making the related sections fail to render in slideouts. The matchers
 * mirror `AssociationType.matches_source` / `matches_target` on the backend.
 */

import { useMemo } from 'react';

import type { AssociationType } from '../core/SchemaClient';
import {
  associationMatchesSource,
  associationMatchesTarget,
  buildEntityKindMap,
  getEntityKinds,
} from '../utils/associationMatching';
import { useAssociationTypes } from './useAssociationTypes';
import { useObjectTypes } from './useObjectTypes';

export interface AssociationMatcher {
  /** True when the association's source side matches the given entity type. */
  (at: AssociationType, entityTypeName: string): boolean;
}

export interface UseAssociationMatchersResult {
  /** All association types for the org (from `useAssociationTypes`). */
  associationTypes: AssociationType[];
  /** True while either the association-types or object-types query is loading. */
  isLoading: boolean;
  /**
   * True once the association-types query has settled (React Query
   * `isFetched`). Callers degrading saved lookup columns use this to stay
   * lenient while the association list is still loading (a not-yet-fetched,
   * transiently-empty list must not drop a valid saved column).
   */
  associationTypesFetched: boolean;
  /**
   * First error from either the association-types or object-types query.
   * Object-type errors are surfaced too: if `useObjectTypes` fails, the
   * entity-kind map is empty and kind-based matching silently degrades to
   * type-name-only — callers need to know.
   */
  error: unknown;
  /** Match the association's source side against an entity type. */
  matchesSource: AssociationMatcher;
  /** Match the association's target side against an entity type. */
  matchesTarget: AssociationMatcher;
  /** Map of entity_type_name → entity_kind[] for kind-based predicate helpers. */
  entityKindMap: ReadonlyMap<string, readonly string[]>;
}

/**
 * Combined hook returning association types + pre-bound matchers.
 *
 * Both `useAssociationTypes` and `useObjectTypes` are individually cached with
 * a 5-minute staleTime and persist to localStorage, so calling this hook from
 * many places is cheap.
 */
export function useAssociationMatchers(
  orgId: number | undefined
): UseAssociationMatchersResult {
  const {
    associationTypes,
    isLoading: associationTypesLoading,
    isFetched: associationTypesFetched,
    error: associationTypesError,
  } = useAssociationTypes(orgId);
  const objectTypesQuery = useObjectTypes(orgId);

  const entityKindMap = useMemo(() => {
    const allObjectTypes = objectTypesQuery.data?.allObjectTypes ?? [];
    return buildEntityKindMap(allObjectTypes);
  }, [objectTypesQuery.data?.allObjectTypes]);

  const matchesSource = useMemo<AssociationMatcher>(
    () => (at, entityTypeName) =>
      associationMatchesSource(
        at,
        entityTypeName,
        getEntityKinds(entityTypeName, entityKindMap)
      ),
    [entityKindMap]
  );

  const matchesTarget = useMemo<AssociationMatcher>(
    () => (at, entityTypeName) =>
      associationMatchesTarget(
        at,
        entityTypeName,
        getEntityKinds(entityTypeName, entityKindMap)
      ),
    [entityKindMap]
  );

  return {
    associationTypes,
    isLoading: associationTypesLoading || objectTypesQuery.isLoading,
    associationTypesFetched,
    error: associationTypesError ?? objectTypesQuery.error,
    matchesSource,
    matchesTarget,
    entityKindMap,
  };
}
